import React, { FC } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CellularOtherBill, CellularPayPrompt } from './types'
import { AlertTriangleIcon, WifiOffIcon } from './icons'
import { color } from './tokens'
import {
  NextInvoiceLine,
  PromptButton,
  PromptDialog,
  PromptExtras,
  PromptFooter,
  PromptHeader,
  SummaryBox
} from './dialog-parts'
import { LockNotice } from './lock-notice'
import { SimStatusNotice } from './sim-status-notice'
import { simStageMessage } from './sim-status'
import { isLocked } from './is-locked'
import { OwedDescription, owedTitle } from './owed-description'
import { billsByController, ControllerBill, renewalText } from './billed-controllers'
import { formatDate, formatMoney, formatPeriod, formatWholeMoney } from './format'

interface OwedDialogProps {
  open: boolean
  prompt: CellularPayPrompt
  onPay: () => void
  onGiveUp: () => void
  onRemindLater: () => void
}

const TITLE_ID = 'cellular-owed-title'
const DESCRIPTION_ID = 'cellular-owed-description'

const useBillStyles = makeStyles({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: 14,
    lineHeight: '20px'
  },
  period: { color: color.mutedForeground },
  amount: { fontWeight: 500, color: color.foreground },
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${color.border}`,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600
  },
  note: { margin: '8px 0 0', fontSize: 12, lineHeight: '16px', color: color.mutedForeground },
  subtotal: { marginTop: 8, paddingTop: 8, borderTop: `1px solid ${color.border}` },
  struck: { color: color.mutedForeground, textDecoration: 'line-through' },
  credit: { fontWeight: 500, color: color.success },
  saving: {
    margin: '8px 0 0',
    padding: '6px 8px',
    borderRadius: 6,
    background: color.success10,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    color: color.success
  }
})

// "Also owed: $720.00 back-bill, due September 26, 2026" — so a customer with two
// back-bills on one subscription (D37) knows the second one is coming.
const otherBillText = (bill: CellularOtherBill, currency: string): string => {
  const due = formatDate(bill.dueDate)
  return (
    `Also owed: ${formatMoney(bill.amountDueUsd, currency)} ` +
    `${bill.isBackbill ? 'back-bill' : 'renewal'}${due ? `, due ${due}` : ''}`
  )
}

const OtherBills: FC<{ prompt: CellularPayPrompt; className: string }> = ({ prompt, className }) => (
  <>
    {(prompt.otherBills || []).map(bill => (
      <p
        className={className}
        data-testid={`cellular-other-bill-${bill.invoiceId}`}
        key={bill.invoiceId}
      >
        {otherBillText(bill, prompt.currency)}
      </p>
    ))}
  </>
)

// Every missed year — billed ones and the ones the two-year cap forgave — then, when
// something was forgiven, the original amount struck through and a courtesy credit
// (the PM's Lovable design, 2026-09-30).
const PeriodsSummary: FC<{ prompt: CellularPayPrompt }> = ({ prompt }) => {
  const classes = useBillStyles()
  const credit = prompt.courtesyCreditUsd || 0
  const byController = billsByController(prompt)
  const rows = byController
    ? []
    : [...(prompt.forgivenPeriods || []), ...prompt.periods].sort((a, b) => a.start.localeCompare(b.start))
  const renewal = renewalText(prompt)
  return (
    <SummaryBox>
      {byController && <ControllerBill prompt={prompt} />}
      {rows.map(period => (
        <div
          className={classes.row}
          key={`${period.start}-${period.end}-${period.description || ''}`}
        >
          <span className={classes.period}>{period.description || formatPeriod(period)}</span>
          <span className={classes.amount}>{formatMoney(period.amountUsd, prompt.currency)}</span>
        </div>
      ))}
      {credit > 0 && (
        <>
          <div className={`${classes.row} ${classes.subtotal}`}>
            <span className={classes.period}>Original amount</span>
            <span
              className={classes.struck}
              data-testid='cellular-original-amount'
            >
              {formatMoney(prompt.amountDueUsd + credit, prompt.currency)}
            </span>
          </div>
          <div className={`${classes.row} ${classes.credit}`}>
            <span>Courtesy credit</span>
            <span data-testid='cellular-courtesy-credit'>-{formatMoney(credit, prompt.currency)}</span>
          </div>
        </>
      )}
      <div className={classes.total}>
        <span>Total due</span>
        <span data-testid='cellular-total-due'>{formatMoney(prompt.amountDueUsd, prompt.currency)}</span>
      </div>
      {credit > 0 && (
        <p className={classes.saving}>
          {`We limited your past-due charges to two years, saving you ${formatWholeMoney(
            credit,
            prompt.currency
          )}.`}
        </p>
      )}
      {renewal && (
        <p
          className={classes.note}
          data-testid='cellular-next-renewal'
        >
          {renewal}
        </p>
      )}
      {prompt.otherUnpaidInvoices > 0 && (
        <p
          className={classes.note}
          data-testid='cellular-other-bills'
        >
          {`This is one of ${prompt.otherUnpaidInvoices + 1} cellular bills on your account, `}
          {`totalling ${formatMoney(prompt.accountTotalDueUsd, prompt.currency)}. `}
          The rest come up once this one is paid.
        </p>
      )}
      <OtherBills
        className={classes.note}
        prompt={prompt}
      />
    </SummaryBox>
  )
}

export const OwedDialog: FC<OwedDialogProps> = ({ open, prompt, onPay, onGiveUp, onRemindLater }) => {
  // Only a blocking back-bill locks; a renewal opened from its banner can always be closed.
  const locked = isLocked(prompt)
  return (
    <PromptDialog
      blocking={locked}
      describedBy={DESCRIPTION_ID}
      labelledBy={TITLE_ID}
      open={open}
    >
      <PromptHeader
        descriptionId={DESCRIPTION_ID}
        icon={<AlertTriangleIcon size={28} />}
        id={TITLE_ID}
        title={owedTitle(prompt)}
        tone='warning'
      >
        <OwedDescription prompt={prompt} />
      </PromptHeader>
      <SimStatusNotice
        message={simStageMessage(prompt)}
        prompt={prompt}
      />
      <PeriodsSummary prompt={prompt} />
      <NextInvoiceLine date={prompt.nextInvoiceDate} />
      <LockNotice prompt={prompt} />
      <PromptFooter>
        <PromptButton
          onClick={onGiveUp}
          variant='ghost'
        >
          <WifiOffIcon />
          Give up cellular connection
        </PromptButton>
        <PromptButton
          onClick={onPay}
          variant='primary'
        >
          Pay Now
        </PromptButton>
      </PromptFooter>
      {/* Not in the mock (always blocking there): a prompt that does not lock can be snoozed. No close "×". */}
      {!locked && (
        <PromptExtras>
          <PromptButton
            onClick={onRemindLater}
            variant='text'
          >
            {/* A renewal never pops up by itself, so there is nothing to snooze. */}
            {prompt.isBackbill ? 'Remind me later' : 'Close'}
          </PromptButton>
        </PromptExtras>
      )}
    </PromptDialog>
  )
}
